// ============================================================================
// EVENT FORM UTILITIES - Shared utilities for event create/edit forms
// ============================================================================

import moment from 'moment';
import { toJsDate } from './dateUtils';
import {
  DEFAULT_FORM_VALUES,
  getEventTypeName,
  getDateTypeName,
  isLayoutRequired,
  getLayoutType,
} from '../constants/events';

/**
 * Generate a random session ID
 */
export const generateSessionID = () => {
  // Capital letters and numbers, length 12
  return Math.random()
    .toString(36)
    .replace(/[^a-z0-9]+/g, '')
    .substring(0, 12)
    .toUpperCase();
};

/**
 * Calculate end date based on start date and duration
 */
export const calculateEndDate = (startDate, duration) => {
  if (!startDate || duration == null || duration === '') return null;

  // Validate the start date using moment
  if (!moment(startDate).isValid()) return null;

  const mins = Number(duration);
  if (!Number.isFinite(mins) || mins < 0) return null;

  const start = new Date(startDate);
  const end = new Date(start.getTime() + mins * 60000);

  // Validate the calculated end date
  if (isNaN(end.getTime())) return null;

  return end;
};

const toApiDate = (value) => {
  if (value == null || value === '') return undefined;
  const m = moment(value);
  return m.isValid() ? m.toISOString() : undefined;
};

const STOCK_TICKET_GROUPS = ['deal', 'guestlist', 'general', 'vip', 'extras', 'invitation'];

export const normalizeEventType = (value) => {
  if (value && typeof value === 'object' && 'target' in value) {
    return normalizeEventType(value.target?.value);
  }
  if (value == null || value === '') return value;
  const normalized = Number(value);
  return Number.isNaN(normalized) ? value : normalized;
};

/**
 * Ensure every ticket product has a stable id before persisting stock.
 */
export const ensureStockTicketIds = (stock) => {
  if (!stock || typeof stock !== 'object') return stock;
  if (Array.isArray(stock)) {
    return stock.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const id = item.id && String(item.id).trim();
      return id ? item : { ...item, id: generateSessionID().slice(0, 8) };
    });
  }

  const next = { ...stock };
  STOCK_TICKET_GROUPS.forEach((group) => {
    if (!Array.isArray(next[group])) return;
    next[group] = next[group].map((item) => {
      if (!item || typeof item !== 'object') return item;
      const id = item.id && String(item.id).trim();
      return id ? item : { ...item, id: generateSessionID().slice(0, 8) };
    });
  });
  return next;
};

/**
 * Normalize coupon payloads for API / web (YYYY-MM-DD activeUntil).
 */
export const normalizeEventCoupons = (coupons) => {
  if (!Array.isArray(coupons)) return coupons;
  return coupons.map((coupon) => {
    if (!coupon || typeof coupon !== 'object') return coupon;
    const activeUntil = coupon.activeUntil
      ? moment(coupon.activeUntil).format('YYYY-MM-DD')
      : coupon.activeUntil;
    return {
      ...coupon,
      activeUntil,
      used: coupon.used ?? 0,
    };
  });
};

/**
 * Transform form data for API submission
 */
export const transformEventData = (data, eventType, dateType) => {
  let toReturn = { ...data };
  const normalizedEventType = normalizeEventType(eventType ?? data?.type);

  // Handle tickets transformation
  if (normalizedEventType === 1 || normalizedEventType === 2) {
    const tickets = data.tickets;
    if (tickets) {
      const newTickets = {
        deal: tickets.deal || [],
        guestlist: tickets.guestlist || [],
        general: tickets.general || [],
        vip: tickets.vip || [],
        extras: tickets.extras || [],
        invitation: tickets.invitation || [],
      };
      toReturn.tickets = newTickets;
    }
    toReturn.tickets = ensureStockTicketIds(toReturn.tickets);
  } else if (normalizedEventType === 3 && Array.isArray(data.tickets)) {
    toReturn.tickets = ensureStockTicketIds(data.tickets);
  }

  if (Array.isArray(data.coupons)) {
    toReturn.coupons = normalizeEventCoupons(data.coupons);
  }

  toReturn.refundDeadlineAt = toApiDate(data.refundDeadlineAt);

  // Handle dates transformation
  if (dateType === 'oneoff') {
    const start = data.startdate;
    const duration = data.duration;
    // Her zaman start+duration'dan end hesapla. Eski/yanlis enddate kalirsa
    // (end < now, start > now) etkinlik Past listesine duser.
    let end =
      start && duration != null && duration !== ''
        ? calculateEndDate(start, duration)
        : data.enddate;

    if (start && end && moment(end).isSameOrBefore(moment(start))) {
      end = calculateEndDate(start, duration || 180);
    }

    if (start && moment(start).isValid()) {
      // Duzenlemede mevcut session-time id'sini KORU; aksi halde stok/satis/bilet
      // iliskisi (stockID) kopar ve tarih degisikligi her yere yansimaz.
      const existingTimes = data.sessions?.[0]?.times;
      const existingId = Array.isArray(existingTimes) ? existingTimes?.[0]?.id : existingTimes?.id;

      const sessions = [
        {
          startdate: moment(start).format('YYYY-MM-DD'),
          times: [
            {
              isActive: true,
              id: existingId || generateSessionID(),
              time: moment(start).format('HH:mm'),
            },
          ],
        },
      ];
      toReturn.sessions = sessions;
      toReturn.startdate = toApiDate(start);
      toReturn.enddate = toApiDate(end);
      if (duration != null && duration !== '') {
        toReturn.duration = Number(duration) || 0;
      }
    }
  } else if (dateType === 'session') {
    const sessions = normalizeSessions(data.sessions);
    const sessionErrors = validateSessions(data.sessions);
    if (sessionErrors.length > 0 || sessions.length === 0) {
      const err = new Error(sessionErrors[0] || 'Session tarih/saat eksik');
      err.sessionErrors = sessionErrors;
      throw err;
    }
    const { startdate, enddate } = calculateSessionDates(sessions);
    toReturn.sessions = sessions;
    toReturn.startdate = toApiDate(startdate);
    toReturn.enddate = toApiDate(enddate);
    if (!toReturn.startdate || !toReturn.enddate) {
      throw new Error('Session tarihlerinden etkinlik başlangıç/bitiş hesaplanamadı');
    }
  }

  return { ...toReturn, notify: true };
};

/**
 * Transform API data for form display
 */
export const transformEventDataForForm = (record) => {
  if (!record) return DEFAULT_FORM_VALUES;

  // DateTimeInput Date veya ISO ister; API ISO string doner.
  let startdate = toJsDate(record.startdate);
  let enddate = toJsDate(record.enddate);
  let duration = record.duration != null ? Number(record.duration) : record.duration;
  if (duration != null && Number.isNaN(duration)) duration = undefined;

  // Duration yoksa start+end'den turett
  if ((duration == null || duration === '') && startdate && enddate) {
    const mins = Math.round((enddate.getTime() - startdate.getTime()) / 60000);
    if (Number.isFinite(mins) && mins >= 0) duration = mins;
  }

  // Hala duration yoksa oneoff icin 180 dk varsayilan (bitis gosterebilmek icin)
  if (
    (duration == null || duration === '') &&
    startdate &&
    (record.dateType || 'oneoff') === 'oneoff'
  ) {
    duration = 180;
  }

  // Bitis yoksa ama start+duration varsa hesapla (edit'te enddate bos kalmasin)
  if (!enddate && startdate && duration != null && duration !== '') {
    enddate = calculateEndDate(startdate, duration);
  }

  let refundDeadlineAt = record.refundDeadlineAt ? toJsDate(record.refundDeadlineAt) : null;
  if (!refundDeadlineAt && startdate && record.refundDeadlineHours != null) {
    const hours = Number(record.refundDeadlineHours);
    if (Number.isFinite(hours)) {
      refundDeadlineAt = new Date(startdate.getTime() - hours * 60 * 60 * 1000);
    }
  }

  return {
    ...DEFAULT_FORM_VALUES,
    ...record,
    dateType: record.dateType || 'oneoff',
    type: record.type,
    startdate: startdate || null,
    enddate: enddate || null,
    duration: duration != null && duration !== '' ? duration : record.duration,
    // Ensure arrays are properly initialized
    paymentMethods: record.paymentMethods || DEFAULT_FORM_VALUES.paymentMethods,
    organisationCompanies: record.organisationCompanies || [],
    subcategories: record.subcategories || [],
    sessions: normalizeSessions(record.sessions || []),
    tickets: record.tickets || {},
    coupons: (record.coupons || []).map((coupon) => ({
      ...coupon,
      activeUntil: coupon.activeUntil
        ? (toJsDate(coupon.activeUntil) ?? coupon.activeUntil)
        : coupon.activeUntil,
    })),
    refundDeadlineAt,
    rules: record.rules || {},
  };
};

/**
 * Validate event dates
 */
export const validateEventDates = (startDate, endDate) => {
  const errors = [];

  if (!startDate) {
    errors.push('Start date is required');
  } else if (!moment(startDate).isValid()) {
    errors.push('Start date is not a valid date');
  } else if (moment(startDate).isBefore(moment())) {
    errors.push('Start date should be after today');
  }

  if (endDate) {
    if (!moment(endDate).isValid()) {
      errors.push('End date is not a valid date');
    } else if (startDate && moment(endDate).isBefore(moment(startDate))) {
      errors.push('End date should be after start date');
    }
  }

  return errors;
};

/**
 * Nested ArrayInput bazen times'ı "" yapıyor; startdate Date/ISO karışık gelebilir.
 * API/stock için temiz sessions[] üretir.
 */
export const normalizeSessions = (sessions) => {
  if (!Array.isArray(sessions)) return [];

  return sessions
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;

      let startdate = raw.startdate ?? null;
      if (startdate) {
        const m = moment(startdate);
        startdate = m.isValid() ? m.format('YYYY-MM-DD') : null;
      }

      const timesRaw = raw.times;
      const timesList = Array.isArray(timesRaw)
        ? timesRaw
        : timesRaw && typeof timesRaw === 'object'
          ? [timesRaw]
          : [];

      const times = timesList
        .filter((t) => t && typeof t === 'object')
        .map((t) => {
          const time =
            typeof t.time === 'string' && /^\d{1,2}:\d{2}/.test(t.time) ? t.time.slice(0, 5) : null;
          if (!time) return null;
          return {
            id: t.id || generateSessionID(),
            time,
            isActive: t.isActive !== false,
          };
        })
        .filter(Boolean);

      return { startdate, times };
    })
    .filter((s) => s && s.startdate && s.times.length > 0);
};

/**
 * Validate sessions
 */
export const validateSessions = (sessions) => {
  const errors = [];
  const normalized = normalizeSessions(sessions);

  if (!Array.isArray(sessions) || sessions.length === 0) {
    errors.push('En az bir oturum (session) gerekli');
    return errors;
  }

  if (normalized.length === 0) {
    errors.push('Oturumlar geçersiz: her session için tarih (gün) ve en az bir saat gerekli');
    return errors;
  }

  sessions.forEach((session, sessionIndex) => {
    if (!session?.startdate) {
      errors.push(`Session ${sessionIndex + 1}: Tarih gerekli`);
    } else if (!moment(session.startdate).isValid()) {
      errors.push(`Session ${sessionIndex + 1}: Geçersiz tarih`);
    }

    const times = Array.isArray(session?.times) ? session.times : [];
    if (!times.length) {
      errors.push(`Session ${sessionIndex + 1}: En az bir saat gerekli`);
    } else {
      times.forEach((time, timeIndex) => {
        if (!time?.time) {
          errors.push(`Session ${sessionIndex + 1}, Saat ${timeIndex + 1}: Saat gerekli`);
        }
      });
    }
  });

  return errors;
};

/**
 * Validate products/tickets
 */
export const validateProducts = (products, eventType) => {
  const errors = [];
  const normalizedEventType = normalizeEventType(eventType);

  if (normalizedEventType === 1 || normalizedEventType === 2) {
    // Standard/Area Layout Event - validate ticket groups
    const ticketGroups = ['deal', 'guestlist', 'general', 'vip', 'extras', 'invitation'];

    ticketGroups.forEach((group) => {
      if (products[group] && Array.isArray(products[group])) {
        products[group].forEach((ticket, index) => {
          if (!ticket.title?.tr) {
            errors.push(`${group} ticket ${index + 1}: Turkish title is required`);
          }
          if (!ticket.title?.en) {
            errors.push(`${group} ticket ${index + 1}: English title is required`);
          }
          if (!ticket.price || ticket.price < 0) {
            errors.push(`${group} ticket ${index + 1}: Valid price is required`);
          }
          if (!ticket.stock || ticket.stock < 0) {
            errors.push(`${group} ticket ${index + 1}: Valid stock is required`);
          }
        });
      }
    });
  } else if (normalizedEventType === 3) {
    // Seated Event - validate individual tickets
    if (!products || !Array.isArray(products) || products.length === 0) {
      errors.push('At least one ticket is required for seated events');
    } else {
      products.forEach((ticket, index) => {
        if (!ticket.title?.tr) {
          errors.push(`Ticket ${index + 1}: Turkish title is required`);
        }
        if (!ticket.title?.en) {
          errors.push(`Ticket ${index + 1}: English title is required`);
        }
        if (!ticket.price || ticket.price < 0) {
          errors.push(`Ticket ${index + 1}: Valid price is required`);
        }
      });
    }
  }

  return errors;
};

/**
 * Calculate session start and end dates
 */
export const calculateSessionDates = (sessions) => {
  if (!sessions || sessions.length === 0) return { startdate: null, enddate: null };

  // Filter out sessions with invalid dates
  const validSessions = sessions.filter(
    (session) => session.startdate && moment(session.startdate).isValid(),
  );

  if (validSessions.length === 0) return { startdate: null, enddate: null };

  const sortedSessions = [...validSessions].sort((a, b) =>
    moment(a.startdate).diff(moment(b.startdate)),
  );

  const startdate = moment(sortedSessions[0].startdate)
    .set({ hour: 0, minute: 0, second: 0 })
    .toDate();

  const enddate = moment(sortedSessions[sortedSessions.length - 1].startdate)
    .set({ hour: 23, minute: 59, second: 59 })
    .toDate();

  return { startdate, enddate };
};

/**
 * Get form validation rules for a specific field
 */
export const getFieldValidationRules = (fieldName, customRules = {}) => {
  const baseRules = {
    eventName: [
      { required: true, message: 'Event name is required' },
      { maxLength: 100, message: 'Event name must be less than 100 characters' },
    ],
    description: [
      { required: true, message: 'Description is required' },
      { minLength: 10, message: 'Description must be at least 10 characters' },
    ],
    price: [
      { required: true, message: 'Price is required' },
      { min: 0, message: 'Price must be greater than or equal to 0' },
    ],
    stock: [
      { required: true, message: 'Stock is required' },
      { min: 0, message: 'Stock must be greater than or equal to 0' },
    ],
    duration: [
      { required: true, message: 'Duration is required' },
      { min: 1, message: 'Duration must be at least 1 minute' },
    ],
    badgeLabel: [{ maxLength: 23, message: 'Badge label must be at most 23 characters' }],
  };

  return [...(baseRules[fieldName] || []), ...customRules];
};

/**
 * Check if a field should be disabled based on permissions and data
 */
export const shouldFieldBeDisabled = (fieldName, permissions, record, scopedFormData) => {
  const isAdmin = permissions?.crole === 'admin';
  const isOperator = permissions?.crole === 'operator';
  const isOrganisationManager = permissions?.crole === 'organisationCompanyManager';

  switch (fieldName) {
    case 'isActive':
    case 'isVerified':
      return !isAdmin;
    case 'servicefee':
    case 'commissionFee':
      return !isAdmin;
    case 'organisationCompanies':
      return isOrganisationManager;
    case 'price':
      return scopedFormData?.sold > 0;
    case 'code':
    case 'discount':
      return scopedFormData?.used > 0;
    case 'enddate':
      return !isAdmin && !isOperator;
    default:
      return false;
  }
};

/**
 * Get helper text for a field
 */
export const getFieldHelperText = (fieldName, scopedFormData, permissions) => {
  const isAdmin = permissions?.crole === 'admin';
  const isOperator = permissions?.crole === 'operator';

  switch (fieldName) {
    case 'price':
      return scopedFormData?.sold > 0
        ? 'If you need to change the price of a product, you can do so by creating a new product and hiding the old one.'
        : '';
    case 'code':
    case 'discount':
      return scopedFormData?.used > 0
        ? 'If you need to change the coupon code/discount for a USED coupon, you can do so by creating a new one'
        : '';
    case 'enddate':
      return isAdmin || isOperator ? 'This is only editable by admin or operator' : '';
    case 'servicefee':
      return 'Service fee to cover extra operational cost';
    case 'isCommissionExtra':
      return 'Please note that the prices for the event will change if this is turned on.';
    case 'isActive':
      return "Please note that the event should be 'saved' for this change to take effect.";
    case 'isVerified':
      return 'Is event verified by the admin?';
    default:
      return '';
  }
};

/**
 * Format event type and date type for display
 */
export const formatEventTypeDisplay = (eventType, dateType) => {
  const eventTypeName = getEventTypeName(eventType);
  const dateTypeName = getDateTypeName(dateType);
  return `${eventTypeName} (${dateTypeName})`;
};

/**
 * Check if layout tab should be shown
 */
export const shouldShowLayoutTab = (eventType) => {
  return isLayoutRequired(normalizeEventType(eventType));
};

/**
 * Get layout type for event
 */
export const getEventLayoutType = (eventType) => {
  return getLayoutType(normalizeEventType(eventType));
};

const eventFormUtils = {
  generateSessionID,
  calculateEndDate,
  transformEventData,
  transformEventDataForForm,
  normalizeSessions,
  validateEventDates,
  validateSessions,
  validateProducts,
  calculateSessionDates,
  getFieldValidationRules,
  shouldFieldBeDisabled,
  getFieldHelperText,
  formatEventTypeDisplay,
  normalizeEventType,
  shouldShowLayoutTab,
  getEventLayoutType,
};

export default eventFormUtils;
